import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons/faExclamationTriangle";
import { faStar } from "@fortawesome/pro-solid-svg-icons/faStar";
import { faTimes } from "@fortawesome/pro-solid-svg-icons/faTimes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";

import EmailEntitiesMissedFields from "@mapmycustomers/shared/types/email/EmailEntitiesMissedFields";
import { EntitiesSupportedByEmailFeature } from "@mapmycustomers/shared/types/entity";
import { stopEvents } from "@mapmycustomers/shared/util/browser";

import { useConfigProvider } from "../../ConfigProvider";
import ListWithOverflowItemWithShrinking from "../../ListWithOverflowItem/ListWithOverflowItemWithShrinking";
import Popconfirm from "../../Popconfirm";

import styles from "./EmailRecipientList.module.scss";

export interface EmailRecipientListProps {
  action?: React.ReactNode;
  className?: string;
  entities: EntitiesSupportedByEmailFeature[];
  entitiesMissedFields?: EmailEntitiesMissedFields;
  label: string;
  labelTooltip?: React.ReactNode;
  moreText: string;
  onEntityRemove?: (entity: EntitiesSupportedByEmailFeature) => void;
  onSetPrimaryEntity?: (entity: EntitiesSupportedByEmailFeature) => void;
  primaryEntityId?: EntitiesSupportedByEmailFeature["id"];
  primaryTooltip?: React.ReactNode;
  readOnly?: boolean;
  setPrimaryTooltip?: React.ReactNode;
}

const EmailRecipientList = ({
  action,
  className,
  entities,
  entitiesMissedFields,
  label,
  labelTooltip,
  moreText,
  onEntityRemove,
  onSetPrimaryEntity,
  primaryEntityId,
  primaryTooltip,
  readOnly,
  setPrimaryTooltip,
}: EmailRecipientListProps) => {
  const configProvider = useConfigProvider();

  const containerRef = useRef<HTMLDivElement>(null);

  const [extended, setExtended] = useState<boolean>();
  const [somePopconfirmVisible, setSomePopconfirmVisible] = useState<boolean>();

  useEffect(() => {
    if (!entities.length) {
      setExtended(true);
    }
  }, [entities]);

  const dupeIndexes = useMemo(() => {
    const emails = entities.map((entity) => entity.email);
    return [...emails]
      .map((email, i) => (emails.filter((e) => e === email).length > 1 ? i : -1))
      .filter((i) => i !== -1);
  }, [entities]);

  const getEntityWarningText = useCallback(
    (entity: EntitiesSupportedByEmailFeature, indexInList: number) => {
      const duplicateText = dupeIndexes.includes(indexInList)
        ? configProvider.formatMessage("ui.emailRecipientList.waringText.tooltip.duplicate")
        : undefined;
      const hasMissingFieldsText = entitiesMissedFields?.[entity.id]
        ? configProvider.formatMessage("ui.emailRecipientList.waringText.tooltip.missingFields")
        : undefined;
      if (duplicateText || hasMissingFieldsText) {
        return (
          <div>
            <b>
              {duplicateText && hasMissingFieldsText
                ? configProvider.formatMessage("ui.emailRecipientList.waringText.tooltip.title")
                : (duplicateText ?? hasMissingFieldsText)}
            </b>
            <br />
            {duplicateText && hasMissingFieldsText ? (
              <ol className={styles.warningPoints}>
                <li>{duplicateText}</li>
                <li>{hasMissingFieldsText}</li>
              </ol>
            ) : (
              <span>
                {duplicateText
                  ? configProvider.formatMessage(
                      "ui.emailRecipientList.waringText.tooltip.duplicate.description"
                    )
                  : configProvider.formatMessage(
                      "ui.emailRecipientList.waringText.tooltip.missingFields.description"
                    )}
              </span>
            )}
          </div>
        );
      }
      return undefined;
    },
    [configProvider, dupeIndexes, entitiesMissedFields]
  );

  const getEntityDisplay = useCallback((entity: EntitiesSupportedByEmailFeature) => {
    return `${entity.name} (${entity.email})`;
  }, []);

  // Expanding unmounts the overflow chip, so focus has to land on the container to stay in the list.
  const extend = useCallback(() => {
    setExtended(true);
    containerRef.current?.focus();
  }, []);

  const handleOverflowRender = useCallback(
    (count: number) => {
      const hasHiddenHighlightedItem = entities.some(
        (entity, i) => i >= entities.length - count && !!getEntityWarningText(entity, i)
      );
      return (
        // A div, not a button, so the existing styling holds; role and key handling keep it reachable.
        <div
          className={cn(styles.more, {
            [styles.moreWithDuplicates]: hasHiddenHighlightedItem,
          })}
          onClick={extend}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              extend();
            }
          }}
          role="button"
          tabIndex={0}
        >
          {hasHiddenHighlightedItem && (
            <FontAwesomeIcon className={styles.warningIcon} icon={faExclamationTriangle} />
          )}
          <span>{count}</span> <span>{moreText}</span>
        </div>
      );
    },
    [entities, extend, getEntityWarningText, moreText]
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      // moving focus between the list's own nodes is not leaving the list
      if (event.currentTarget.contains(event.relatedTarget)) {
        return;
      }
      if (entities.length) {
        setExtended(false);
      }
    },
    [entities]
  );

  // Only sets the state: onFocus is focusin, which bubbles, so moving focus here would strip it
  // from any child of the container that just received it — the action slot included.
  const handleFocus = useCallback(() => {
    if (entities.length) {
      setExtended(true);
    }
  }, [entities]);

  return (
    <div
      className={cn(className, styles.container)}
      onBlur={handleBlur}
      onFocus={readOnly ? undefined : handleFocus}
      ref={containerRef}
      tabIndex={-1}
    >
      <Tooltip title={labelTooltip && entities.length > 1 ? labelTooltip : null}>
        <div>{label}:</div>
      </Tooltip>
      {extended || somePopconfirmVisible ? (
        <div className={styles.extendedContainer}>
          {entities.map((entity, i) => {
            const warningText = getEntityWarningText(entity, i);
            return (
              <Popconfirm
                danger
                description={warningText}
                key={entity.id}
                okButtonProps={{ disabled: entities.length <= 1 }}
                okText={configProvider.formatMessage("ui.emailRecipientList.waringText.tooltip.ok")}
                onConfirm={() => {
                  onEntityRemove?.(entity);
                }}
                onOpenChange={setSomePopconfirmVisible}
                overlayClassName={styles.issuePopconfirm}
                trigger={warningText && !readOnly ? ["click"] : []}
              >
                <span
                  className={cn(styles.email, styles.normalEmail, {
                    [styles.warn]: !!warningText,
                  })}
                >
                  {!!warningText && (
                    <FontAwesomeIcon className={styles.warningIcon} icon={faExclamationTriangle} />
                  )}
                  <span className={styles.recipientName}>{getEntityDisplay(entity)}</span>
                  {onSetPrimaryEntity && entities.length > 1 && !readOnly && (
                    <Tooltip
                      title={entity.id === primaryEntityId ? primaryTooltip : setPrimaryTooltip}
                    >
                      <span
                        className={cn(styles.primaryIcon, {
                          [styles.primaryIconActive]: entity.id === primaryEntityId,
                        })}
                        onClick={(e) => {
                          stopEvents(e);
                          onSetPrimaryEntity(entity);
                        }}
                      >
                        <FontAwesomeIcon icon={faStar} />
                      </span>
                    </Tooltip>
                  )}
                  {entities.length > 1 && !readOnly && (
                    <span
                      className={styles.deleteIcon}
                      onClick={(e) => {
                        stopEvents(e);
                        onEntityRemove?.(entity);
                      }}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </span>
                  )}
                </span>
              </Popconfirm>
            );
          })}
          {action}
        </div>
      ) : (
        <ListWithOverflowItemWithShrinking
          className={styles.recipients}
          overflowRender={handleOverflowRender}
        >
          {entities.map((entity, i) => {
            const warning = !!getEntityWarningText(entity, i);
            return (
              <span
                className={cn(styles.email, styles.inlineEmail, {
                  [styles.inlineEmailDuplicate]: warning,
                })}
                key={entity.id}
              >
                {warning && (
                  <FontAwesomeIcon className={styles.warningIcon} icon={faExclamationTriangle} />
                )}
                {getEntityDisplay(entity)}
              </span>
            );
          })}
        </ListWithOverflowItemWithShrinking>
      )}
    </div>
  );
};

export default EmailRecipientList;
